import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { RouteCodeEnum } from '@models/menu/menuModel';
import { UserStore } from '@signal-store';

export const userResolver: ResolveFn<boolean> = () => {
  const userStore = inject(UserStore);
  return toObservable(userStore.userData).pipe(
    filter(userData => !!userData?.id),
    take(1),
    map(() => true),
  );
};

const getModuleIdFromRoute = (route: ActivatedRouteSnapshot): number | null => {
  const routeModuleId = route.paramMap.get('moduleId') ?? route.parent?.paramMap.get('moduleId');
  if (!routeModuleId) {
    return null;
  }

  const parsed = Number(routeModuleId);
  return Number.isFinite(parsed) ? parsed : null;
};

export const menuItemResolver: ResolveFn<boolean> = route => {
  const userStore = inject(UserStore);
  const requestedModuleId = getModuleIdFromRoute(route);
  const requestedMenuCode = route.data?.['menuCode'] as RouteCodeEnum | undefined;

  const syncActiveMenuItem = (
    activeMenuItemModuleId?: number | null,
    activeMenuItemCode?: RouteCodeEnum,
    menuItemsLoaded = false,
  ) => {
    if (!menuItemsLoaded) {
      return;
    }

    if (requestedMenuCode) {
      if (activeMenuItemCode !== requestedMenuCode) {
        userStore.setActiveMenuByCode(requestedMenuCode, false);
      }
      return;
    }

    if (requestedModuleId === null || activeMenuItemModuleId === requestedModuleId) {
      return;
    }
    userStore.setActiveMenuByModuleId(requestedModuleId, false);
  };

  syncActiveMenuItem(
    userStore.menu.activeMenuItem()?.moduleId,
    userStore.menu.activeMenuItem()?.code,
    !!userStore.menuItemsList?.()?.length,
  );

  return combineLatest([toObservable(userStore.menu.activeMenuItem), toObservable(userStore.menuItemsList)]).pipe(
    tap(([activeMenuItem, menuItems]) =>
      syncActiveMenuItem(activeMenuItem?.moduleId, activeMenuItem?.code, !!menuItems?.length),
    ),
    map(([activeMenuItem]) => activeMenuItem),
    filter(item => (requestedMenuCode ? item?.code === requestedMenuCode : !!item?.moduleId)),
    take(1),
    map(() => true),
  );
};
